import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldAlert, Search, FileUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/pages/Landing";
import { api, formatError, fmtDate } from "@/lib/api";

const Row = ({ k, v, mono }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-slate-100 dark:border-slate-800 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className={`sm:text-right break-all ${mono ? "font-mono text-xs" : "font-medium"}`}>{v || "—"}</span>
  </div>
);

const FileCheck = ({ certId }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const check = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      setRes((await api.post(`/verify/${certId}/file`, fd)).data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6">
      <label htmlFor="cert-file" className="flex items-center justify-center gap-2 cursor-pointer rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 transition-colors p-6 text-sm">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />} Check a certificate PDF has not been altered
        <input id="cert-file" data-testid="verify-file-input" type="file" accept="application/pdf" className="sr-only" onChange={(e) => check(e.target.files?.[0])} />
      </label>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      {res && (
        <div data-testid="verify-file-result" className={`mt-3 rounded-lg border p-4 text-sm ${res.match ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300" : "border-red-200 bg-red-50 text-red-700"}`}>
          <p className="font-semibold">{res.match ? "File matches the issued certificate exactly." : "File does NOT match the issued certificate."}</p>
          <p className="font-mono text-xs mt-1 break-all">uploaded {res.uploaded_sha256}</p>
        </div>
      )}
    </div>
  );
};

const BrandHeader = ({ b }) => (
  <div data-testid="verify-brand" className="-mx-6 -mt-6 sm:-mx-8 sm:-mt-8 mb-6 rounded-t-2xl overflow-hidden">
    <div className="px-6 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3" style={{ background: b.color, color: b.text_color }}>
      {b.logo_url
        ? <span className="bg-white rounded-lg px-3 py-2 inline-flex"><img data-testid="verify-brand-logo" src={b.logo_url} alt={b.display_name} className="h-9 max-w-[220px] object-contain" /></span>
        : <p data-testid="verify-brand-name" className="font-display text-xl font-bold">{b.display_name}</p>}
      <p data-testid="verify-trust-mark" className="text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1 bg-white/95 text-emerald-700 rounded-full px-3 py-1"><ShieldCheck size={12} /> Verified by PostalCloud</p>
    </div>
    <p className="text-xs text-slate-500 px-6 sm:px-8 pt-4">This certificate was issued by PostalCloud for a letter sent by {b.display_name}. The integrity check below is performed independently by PostalCloud.</p>
  </div>
);

const Mismatch = ({ page }) => (
  <div data-testid="verify-org-mismatch" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-800 dark:text-amber-300 flex gap-2">
    <ShieldAlert size={16} className="shrink-0 mt-0.5" /> This certificate was not issued for {page.display_name}. The details below show who actually sent it.
  </div>
);

const Result = ({ r, page }) => (
  <div data-testid="verify-result" className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8" style={r.brand ? { borderColor: r.brand.color } : undefined}>
    {r.brand && <BrandHeader b={r.brand} />}
    {page && page.org_id !== r.org_id && <Mismatch page={page.brand} />}
    <div className={`flex items-start gap-3 ${r.valid ? "text-emerald-700 dark:text-emerald-400" : "text-red-600"}`}>
      {r.valid ? <ShieldCheck /> : <ShieldAlert />}
      <div>
        <p data-testid="verify-status" className="font-display text-xl font-bold">{r.valid ? "Genuine certificate — records intact" : "Warning — records failed verification"}</p>
        <p className="text-sm text-slate-500 mt-1">{r.records_verified} of {r.records_total} audit records verified (SHA-256 hash and chain link).</p>
      </div>
    </div>
    <div className="mt-6">
      <Row k="Letter reference" v={r.letter.reference} />
      {r.letter.sending_organisation && <Row k="Sending organisation" v={r.letter.sending_organisation} />}
      <Row k="Sender" v={r.letter.sender_name} />
      <Row k="Recipient" v={r.letter.recipient_initials} />
      <Row k="Status" v={r.letter.status} />
      <Row k="Sent" v={fmtDate(r.letter.sent_at)} />
      <Row k="Delivered (notified)" v={fmtDate(r.letter.notified_at)} />
      <Row k="Claimed" v={fmtDate(r.letter.claimed_at)} />
      <Row k="Opened" v={fmtDate(r.letter.opened_at)} />
      <Row k="Certificate issued" v={`${fmtDate(r.issued_at)} (to ${r.issued_to_role})`} />
      <Row k="Letter file SHA-256" v={r.letter.file_sha256} mono />
      <Row k="Certificate PDF SHA-256" v={r.pdf_sha256} mono />
    </div>
    <FileCheck certId={r.certificate_id} />
  </div>
);

const SignatureCheck = () => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const check = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      setRes((await api.post("/verify-signature", fd)).data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  const ok = res?.signed_by_postalcloud && res?.unaltered;
  return (
    <section className="mt-12" data-testid="signature-check">
      <h2 className="font-display text-lg font-semibold">Check a certificate's digital signature</h2>
      <p className="text-sm text-slate-500 mt-1">Certificates are digitally signed by PostalCloud and independently timestamped. Upload one to confirm it hasn't been changed since it was issued.</p>
      <label htmlFor="sig-file" className="mt-4 flex items-center justify-center gap-2 cursor-pointer rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 transition-colors p-6 text-sm">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />} Upload a certificate PDF
        <input id="sig-file" data-testid="signature-file-input" type="file" accept="application/pdf" className="sr-only" onChange={(e) => check(e.target.files?.[0])} />
      </label>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      {res && (
        <div data-testid="signature-result" className={`mt-3 rounded-lg border p-4 text-sm ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300" : "border-red-200 bg-red-50 text-red-700"}`}>
          <p className="font-semibold">{res.readable === false ? "We couldn't read this PDF. Please upload the original certificate file." : !res.signed ? "This PDF has no digital signature. Certificates issued before signing was introduced aren't signed." : ok ? "Genuine: signed by PostalCloud and unchanged since issue." : "Warning: this PDF has been changed or wasn't signed by PostalCloud."}</p>
          {res.signed && <p className="mt-1">{res.timestamped ? `Independently timestamped ${fmtDate(res.timestamp_time)}.` : "No independent timestamp."}</p>}
          {res.certificate_id && <p className="font-mono text-xs mt-1 break-all">Certificate {res.certificate_id}</p>}
        </div>
      )}
    </section>
  );
};

const OrgHero = ({ b }) => (
  <div data-testid="verify-org-hero" className="rounded-2xl overflow-hidden border mb-10" style={{ borderColor: b.color }}>
    <div className="px-6 sm:px-8 py-8" style={{ background: b.color, color: b.text_color }}>
      {b.logo_url
        ? <span className="bg-white rounded-lg px-4 py-3 inline-flex"><img data-testid="verify-org-logo" src={b.logo_url} alt={b.display_name} className="h-10 max-w-[240px] object-contain" /></span>
        : <p data-testid="verify-org-name" className="font-display text-2xl font-bold">{b.display_name}</p>}
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-5">Verify a {b.display_name} certificate</h1>
    </div>
    <p className="px-6 sm:px-8 py-4 text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2 bg-white dark:bg-slate-900"><ShieldCheck size={15} className="text-emerald-600 shrink-0" /> Checks are run independently by PostalCloud against a tamper-evident record. {b.display_name} cannot change the result.</p>
  </div>
);

export default function Verify() {
  const { certId, orgSlug } = useParams();
  const nav = useNavigate();
  const [id, setId] = useState(certId || "");
  const [r, setR] = useState(null);
  const [page, setPage] = useState(null);
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPage(null);
    setPageError("");
    if (orgSlug) { api.get(`/verify/o/${orgSlug}`).then((x) => setPage(x.data)).catch((e) => setPageError(formatError(e))); }
  }, [orgSlug]);

  useEffect(() => {
    if (!certId) return;
    setBusy(true);
    setError("");
    setR(null);
    api.get(`/verify/${certId}`).then((x) => setR(x.data)).catch((e) => setError(formatError(e))).finally(() => setBusy(false));
  }, [certId]);

  return (
    <div className="min-h-screen flex flex-col">
      <SiteNav />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-16">
        {pageError && <p data-testid="verify-org-error" className="mb-6 text-sm text-red-600 flex gap-2"><ShieldAlert size={16} /> {pageError}</p>}
        {page ? <OrgHero b={page.brand} /> : (<>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Certificate verification</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">Verify a delivery certificate</h1>
        </>)}
        <p className="text-slate-500 mt-3">Enter the certificate ID printed on a PostalCloud delivery certificate to confirm it was issued by us and its records are unaltered.</p>
        <form className="mt-8 flex flex-col sm:flex-row gap-3" onSubmit={(e) => { e.preventDefault(); if (id.trim()) nav(orgSlug ? `/verify/o/${orgSlug}/${id.trim()}` : `/verify/${id.trim()}`); }}>
          <Input data-testid="verify-id-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="e.g. 75ae4d58-2e4f-420b-8639-8c28c27c89e5" className="h-12 font-mono text-sm" />
          <button data-testid="verify-submit-btn" type="submit" style={page ? { background: page.brand.color, color: page.brand.text_color } : undefined} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold hover:bg-emerald-600 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Verify
          </button>
        </form>
        {error && <p data-testid="verify-error" className="mt-6 text-sm text-red-600 flex gap-2"><ShieldAlert size={16} /> {error}</p>}
        {r && <Result r={r} page={page} />}
        <SignatureCheck />
      </main>
      <SiteFooter />
    </div>
  );
}
